'use client';

// Previsualizador del ACTA DE REPARTO con el MISMO patrón que las otras minutas
// (Auto Inhibitorio / decisiones): renderiza el .docx oficial real —con los logos de
// cabecera y pie— vía docx-preview, y permite descargar el .docx o exportar a PDF
// (render → imprimir → «Guardar como PDF»). El acta lleva 2 firmas: Técnico
// administrativo (Elaboró) + Contratista (Quien recibe).

import { useEffect, useRef, useState } from 'react';
import { Download, Eye, FileSignature, Loader2, Printer } from 'lucide-react';
import { toast } from 'sonner';
import type { Solicitud, Firmante } from '@/lib/preRadicacion';
import { blobActaReparto, descargarActaReparto } from '@/lib/plantillas/acta-reparto';
import { PlantillaFaltanteError } from '@/lib/plantillas/motor';
import { Button } from '@/components/ui/button';
import {
  Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';

export default function ActaRepartoDialog({
  solicitud, firmantes, onClose,
}: {
  solicitud: Solicitud;
  firmantes: Firmante[];
  onClose: () => void;
}) {
  const [render, setRender] = useState(false);
  const [expPdf, setExpPdf] = useState(false);
  const previewRef = useRef<HTMLDivElement>(null);
  const yaRenderizo = useRef(false);

  async function previsualizar() {
    setRender(true);
    try {
      const blob = await blobActaReparto(solicitud, firmantes);
      const { renderAsync } = await import('docx-preview');
      // Leemos el ref DESPUÉS del await: en el doble-montaje de StrictMode el div se
      // recrea, así renderizamos en el contenedor vivo (no en uno ya desmontado).
      const host = previewRef.current;
      if (!host) return; // el modal se cerró mientras generaba
      host.innerHTML = '';
      await renderAsync(blob, host, undefined, {
        className: 'docx', inWrapper: true, ignoreLastRenderedPageBreak: true,
      });
    } catch (e) {
      const msg = e instanceof PlantillaFaltanteError
        ? 'Falta la plantilla oficial del acta en assets.'
        : (e instanceof Error ? e.message : undefined);
      toast.error('No se pudo generar la previsualización', { description: msg });
    } finally {
      setRender(false);
    }
  }

  // Previsualización automática al abrir (una sola vez). Se dispara directo al montar
  // —el ref ya está asignado cuando corre el efecto— sin setTimeout ni cleanup que la
  // cancele (evita quedar en blanco por doble-montaje/StrictMode). «Actualizar» sigue
  // disponible para re-generar tras cambios.
  useEffect(() => {
    if (yaRenderizo.current) return;
    yaRenderizo.current = true;
    void previsualizar();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function descargarDocx() {
    try {
      await descargarActaReparto(solicitud, firmantes);
      toast.success('Acta de reparto descargada (.docx).');
    } catch (e) {
      toast.error('No se pudo descargar el acta', {
        description: e instanceof Error ? e.message : undefined,
      });
    }
  }

  // Exporta a PDF renderizando el .docx real (con logos) en una ventana e invocando
  // imprimir → «Guardar como PDF» (cero infraestructura, texto vectorial).
  async function descargarPDF() {
    setExpPdf(true);
    try {
      const blob = await blobActaReparto(solicitud, firmantes);
      const { renderAsync } = await import('docx-preview');
      const w = window.open('', '_blank', 'width=920,height=1000');
      if (!w) { toast.error('Permita las ventanas emergentes para exportar a PDF.'); return; }
      w.document.title = `acta-reparto-${solicitud.radicado_completo ?? solicitud.id}`;
      const st = w.document.createElement('style');
      st.textContent = '.docx-wrapper section.docx > header, .docx-wrapper section.docx > footer { z-index: 2; }';
      w.document.head.appendChild(st);
      await renderAsync(blob, w.document.body, undefined, { className: 'docx', inWrapper: true });
      toast.message('En el diálogo de impresión elija «Guardar como PDF».');
      setTimeout(() => { w.focus(); w.print(); }, 500);
    } catch (e) {
      toast.error('No se pudo exportar a PDF', {
        description: e instanceof Error ? e.message : undefined,
      });
    } finally {
      setExpPdf(false);
    }
  }

  return (
    <Dialog open onOpenChange={(o) => { if (!o) onClose(); }}>
      <DialogContent className="w-[80vw] max-w-[80vw] sm:max-w-[80vw]">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <FileSignature className="size-5 text-primary" /> Acta de reparto
          </DialogTitle>
          <DialogDescription>
            Formato oficial (con logos). Firman el Técnico administrativo (quien elabora)
            y el Contratista asignado.
          </DialogDescription>
        </DialogHeader>

        <div className="flex flex-col gap-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <span className="text-sm font-medium text-muted-foreground">Previsualización</span>
            <div className="flex flex-wrap gap-2">
              <Button size="sm" variant="outline" onClick={previsualizar} disabled={render}>
                {render ? <Loader2 className="size-3.5 animate-spin" /> : <Eye className="size-3.5" />}
                Actualizar
              </Button>
              <Button size="sm" variant="outline" onClick={descargarDocx}>
                <Download className="size-3.5" /> .docx
              </Button>
              <Button size="sm" onClick={descargarPDF} disabled={expPdf}>
                {expPdf ? <Loader2 className="size-3.5 animate-spin" /> : <Printer className="size-3.5" />}
                Descargar PDF
              </Button>
            </div>
          </div>
          {/* El contenedor de docx-preview NO debe tener hijos de React. */}
          <div className="relative max-h-[74vh] overflow-auto rounded-md border bg-neutral-100 text-black">
            {render && (
              <div className="pointer-events-none absolute inset-x-0 top-0 z-10 p-4 text-center text-sm text-gray-500">
                Generando…
              </div>
            )}
            <div ref={previewRef} className="p-2" />
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
}
